'use client';

import { clearCachedCredits } from '@/components/creditsProvider';
import { sendCustomerEvent } from '@/components/newRelic';
import { hideToast, showToast } from '@/redux/navigationSlice';
import { pushUserdata } from '@/redux/userdataSlice';
import { customerJWT, login, loginResponse } from '@repo/customer-api';
import { loginErrorMessage } from '@repo/ui';
import { deleteClientCookie, failureDetails, parseJwt, setClientCookie, splitFullName } from '@repo/utils';
import { useRouter } from 'next/navigation';
import { useCallback, useEffect } from 'react';
import { useDispatch } from 'react-redux';
import { DynamicLogin } from '../components/DynamicLogin';

/**
 * Fetch and cache offers asynchronously without blocking form submission
 */
async function fetchAndCacheOffers(): Promise<void> {
  try {
    // disabled for now
    //const { ratings } = await offers();
    //setClientCookie('offers', JSON.stringify(ratings));
  } catch (error) {
    console.error('Failed to cache offers:', error);
    // Silently fail - offers cache is optional for UX
  }
}

export default function Page(): React.JSX.Element {
  const router = useRouter();
  const dispatch = useDispatch();

  useEffect(() => {
    deleteClientCookie(['offers', 'token-customer', 'userId-customer', 'username-customer']);
    // Drops pages cached in the browser (kept 30s, see next.config.js), so the next user never sees the last one's data
    router.refresh();
    clearCachedCredits();
    // Sections send here with ?expired=1 when the session ended without a manual logout
    if (new URLSearchParams(window.location.search).has('expired')) {
      dispatch(showToast({ kind: 'info', text: 'Tu sesión expiró. Ingresá de nuevo.' }));
      router.replace('/login');
    }
  }, [dispatch, router]);

  // Memoized activation button click handler
  const handleActivatAccount = useCallback(() => {
    router.push('/retrieve_activation');
  }, [router]);

  // Memoized login callback
  const handleLoginSuccess = useCallback(
    async (response: loginResponse) => {
      // An error toast from a failed attempt stays until closed, so drop it before leaving the login
      dispatch(hideToast());

      // Decode token and extract user info
      const decodedToken = parseJwt(response.token) as customerJWT;
      const expireDate = new Date(decodedToken.exp * 1000).toUTCString();

      // Set authentication cookies (blocking, critical path)
      setClientCookie('token-customer', response.token, expireDate);
      setClientCookie('userId-customer', `${decodedToken.userId}`);

      // Update Redux state with user data (blocking, critical path)
      const { firstName, fullName } = splitFullName(decodedToken.fullName);
      dispatch(
        pushUserdata({
          username: fullName,
          firstName,
          userId: `${decodedToken.userId}`,
        }),
      );

      // Send analytics event
      sendCustomerEvent({ type: 'interaction', subtype: 'login' });

      // Fire off offers fetch in the background (non-blocking)
      // This allows form submission feedback to show immediately
      fetchAndCacheOffers().then(
        () => {
          // Navigate after critical operations complete
          router.push('/loans/operations');
        },
        (error) => {
          console.error('Navigation error:', error);
          // Still navigate even if offers fetch fails
          router.push('/loans/operations');
        },
      );
    },
    [router, dispatch],
  );

  // Memoized forgot password handler
  const handleForgotPassword = useCallback(() => {
    router.push('/forgot_password');
  }, [router]);

  // Wrong credentials show under the password field; this only covers connectivity and server failures
  const handleLoginError = useCallback(
    (error: unknown) => {
      // Login rejects with either the failed api() response or a thrown error
      const response = error && typeof error === 'object' && !(error instanceof Error) ? error : undefined;
      sendCustomerEvent({ type: 'error', subtype: 'login', data: failureDetails(response, error) });
      dispatch(showToast({ kind: 'error', text: loginErrorMessage(error) }));
    },
    [dispatch],
  );

  return (
    <DynamicLogin
      connector={login}
      extraButtons={[
        {
          label: 'Activar mi cuenta',
          onClick: handleActivatAccount,
        },
      ]}
      callback={handleLoginSuccess}
      forgotPassword={handleForgotPassword}
      onError={handleLoginError}
      credentialsErrorText="DNI y/o contraseña incorrectos"
    />
  );
}
